import { apiClient } from '../api/fetchClient';
import type { Page } from '../common/page/page.model';
import type { Pageable } from '../common/page/pageable.model';
import type { Comment } from './model/comment.interface';
import type { ImageUploadResponse } from './model/image-upload-response.interface';
import type { PerksResponse } from './model/perks-response.interface';
import type { Author, Post, PostCount, Tag } from './model/post.interface';
import type { PostActionSearchRequest } from './model/post-action-search-request';
import type { PostFilter } from './model/post-filter.model';
import type { SubmissionRequest, SubmissionResponse } from './model/submission.interface';
import type { ContentTab, ContentType } from './model/tab.interface';
import type { User, UserUpdateRequest } from './model/user.interface';
import type { UserActionRequest } from './model/user-action.inteface';
import { PerksHelper } from './perks.helper';

export class PerksService {
  async loadTabs(): Promise<ContentTab[]> {
    const url = new URL('/apps/single/v3/content/tabs', window.location.origin);
    return await apiClient.get<ContentTab[]>(url.pathname + url.search);
  }

  async loadTabContent(contentType: ContentType, cursor?: string | null, size = PerksHelper.TAB_CONTENT_PAGE_SIZE): Promise<Page<any>> {
    const url = new URL(`/apps/single/v3/content/tab/${contentType}`, window.location.origin);
    const params: Record<string, any> = { size };
    if (cursor) params.cursor = cursor;
    return await apiClient.get<Page<any>>(url.pathname + url.search, params);
  }

  async loadPerks(query?: string): Promise<PerksResponse> {
    const url = new URL('/apps/single/v3/content/perks', window.location.origin);

    if (query) {
      url.searchParams.append('query', query);
    }

    return await apiClient
      .postText(url.pathname + url.search)
      .then((response) => JSON.parse(response))
      .then((perks) => ({
        ...perks,
        posts: {
          ...perks.posts,
          content: perks.posts?.content?.map(PerksHelper.sortPostContents) ?? [],
        },
      }));
  }

  async loadPosts(pageable: Pageable, filter?: PostFilter): Promise<Page<Post>> {
    const params: any = {
      page: pageable.pageNumber.toString(),
      size: pageable.pageSize.toString(),
    };

    // Having these return 500
    // if (pageable.sort) {
    //     params.sort = pageable.sort.toQueryParamValues().join(',');
    // }

    if (filter?.query) {
      params.query = filter?.query;
    }

    if (filter?.types && filter?.types.length > 0) {
      params.types = filter?.types.join(',');
    }

    if (filter?.authors && filter?.authors.length > 0) {
      params.authors = filter?.authors.join(',');
    }

    if (filter?.tagIds && filter?.tagIds.length > 0) {
      params.tagIds = filter?.tagIds.join(',');
    }

    const url = new URL('/apps/single/v3/community/post', window.location.origin);
    return await apiClient.get<Page<Post>>(url.pathname + url.search, params);
  }

  async loadPopularPosts(
    request: PostActionSearchRequest,
    pageable: Pageable
  ): Promise<Page<Post>> {
    const url = new URL('/apps/single/v3/community/post/search/actions', window.location.origin);
    const params: any = {
      page: pageable.pageNumber.toString(),
      size: pageable.pageSize.toString(),
    };

    if (pageable.sort) {
      params.sort = pageable.sort.toQueryParamValues().join(',');
    }
    return await apiClient.post<Page<Post>>(url.pathname + url.search, request, params);
  }

  async loadPostById(postId: string): Promise<Post> {
    const url = new URL(`/apps/single/v3/community/post/${postId}`, window.location.origin);
    return await apiClient.get<Post>(url.pathname + url.search);
  }

  async getPostComments(
    postId: string,
    parentCommentId: string | null,
    commentDepth: number,
    childPageSize: number,
    pageable: Pageable
  ): Promise<Page<Comment>> {
    const url = new URL(`/apps/single/v3/community/post/${postId}/comment`, window.location.origin);
    const params: any = {
      page: pageable.pageNumber,
      size: pageable.pageSize,
      commentDepth,
      childPageSize,
      parentCommentId,
    };

    // Add sort parameters if present
    const sortParams = pageable.sort?.toQueryParamValues();
    if (sortParams && sortParams.length > 0) {
      params.sort = sortParams;
    }

    return await apiClient.get<Page<Comment>>(url.pathname + url.search, params);
  }

  async loadPostCounts(postId?: string): Promise<PostCount[]> {
    const url = new URL('/apps/single/v3/community/count', window.location.origin);
    return await apiClient.get<PostCount[]>(url.pathname + url.search, { postId });
  }

  async postComment(
    postId: string,
    parentCommentId: string | null,
    comment: Comment
  ): Promise<Comment> {
    const url = new URL(`/apps/single/v3/community/post/${postId}/comment`, window.location.origin);

    const params = parentCommentId ? { parentCommentId } : undefined;

    return await apiClient.post<Comment>(url.pathname + url.search, comment, params);
  }

  async archiveComment(postId: string, commentId: string): Promise<Comment> {
    const url = new URL(
      `/apps/single/v3/community/post/${postId}/comment/${commentId}/archive`,
      window.location.origin
    );
    return await apiClient.post<Comment>(url.pathname + url.search);
  }

  async postUserAction(req: UserActionRequest): Promise<any> {
    const url = new URL(`/apps/single/v3/community/user/action`, window.location.origin);
    return await apiClient.post(url.pathname + url.search, req);
  }

  async uploadAvatar(file: File, filename: string): Promise<ImageUploadResponse> {
    return await apiClient
      .get<ImageUploadResponse>(
        `/apps/single/v3/community/user/image-upload-post/${filename}`,
        { contentType: file.type }
      )
      .then(async (imageUploadResponse) => {
        const formData = new FormData();
        for (const [name, value] of Object.entries(imageUploadResponse.uploadFields ?? {})) {
          formData.append(name, value);
        }
        formData.append('file', file);
        const uploadResponse = await fetch(imageUploadResponse.uploadUrl, {
          method: 'POST',
          body: formData,
        });
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.status}`);
        }
        if (imageUploadResponse.imageFileSetId) {
          await this.pollImageExists(imageUploadResponse.imageFileSetId).catch(() => {
            // Image may still be processing; proceed to updateUser anyway
          });
        }
        return imageUploadResponse;
      })
      .catch((error) => {
        console.error('Error uploading avatar:', error);
        throw error;
      });
  }

  pollImageExists(imageFileSetId: string): Promise<any> {
    const url = new URL(
      `/apps/single/v3/community/image/${imageFileSetId}/exists`,
      window.location.origin
    );
    return this.pollUntilSuccess({
      loadFn: () => apiClient.getStatus(url.pathname + url.search),
    });
  }

  async getUser(): Promise<User> {
    const url = new URL('/apps/single/v3/community/user', window.location.origin);
    return await apiClient.get<User>(url.pathname + url.search);
  }

  /** blank or missing fields are left unchanged by the backend, so a partial update is safe */
  async updateUser(user: Partial<UserUpdateRequest>): Promise<User> {
    const url = new URL('/apps/single/v3/community/user', window.location.origin);
    return await apiClient.post<User>(url.pathname + url.search, user);
  }

  async createSubmission(request: SubmissionRequest): Promise<SubmissionResponse> {
    const url = new URL('/apps/single/v3/community/submission', window.location.origin);
    return await apiClient.post<SubmissionResponse>(url.pathname + url.search, request);
  }

  async getCustomContent(id: string): Promise<string> {
    const url = new URL(`/apps/single/v3/community/custom-content/${id}`, window.location.origin);
    return await apiClient.getText(url.pathname + url.search);
  }

  async getAuthors(pageable: Pageable): Promise<Page<Author>> {
    const url = new URL(`/apps/single/v3/community/authors`, window.location.origin);
    const params: any = {
      page: pageable.pageNumber.toString(),
      size: pageable.pageSize.toString(),
    };

    if (pageable.sort) {
      params.sort = pageable.sort.toQueryParamValues().join(',');
    }
    return await apiClient.get<Page<Author>>(url.pathname + url.search, params);
  }

  async getTags(pageable: Pageable): Promise<Page<Tag>> {
    const url = new URL(`/apps/single/v3/community/tag`, window.location.origin);
    const params: any = {
      page: pageable.pageNumber.toString(),
      size: pageable.pageSize.toString(),
    };

    if (pageable.sort) {
      params.sort = pageable.sort.toQueryParamValues().join(',');
    }
    return await apiClient.get<Page<Tag>>(url.pathname + url.search, params);
  }

  async pollUntilSuccess({
    loadFn,
    interval = 1000,
    timeout = 60000,
    checkSuccess = (status: number) => status === 200,
  }: {
    loadFn: () => Promise<any>;
    interval?: number;
    timeout?: number;
    checkSuccess?: (response: any) => boolean;
  }) {
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      try {
        const loadResponse = await loadFn();
        if (checkSuccess(loadResponse)) {
          return loadResponse;
        }
      } catch (error) {
        // Continue polling on error - image might not be processed yet
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }

    throw new Error(`Polling timed out after ${timeout}ms`);
  }
}
